import { Link } from 'react-router-dom';
import { ChevronRight, Phone, Mail, Heart, Shield, Wrench } from 'lucide-react';

const About = () => {
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="relative bg-slate-900 py-24 md:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_c1d7fe75-4e50-41d3-9936-35f1cf314c96/artifacts/r312sf5m_P1050337.JPG)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-orange-500 font-heading text-sm uppercase tracking-widest mb-3">
              Our Story
            </p>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About Us
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              A small, family-run business building Alaska's toughest boats with 
              quality, integrity, and functionality.
            </p>
          </div>
        </div>
      </section>

      {/* Main Story */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Built on Faith & Family
              </h2>
              <div className="space-y-6 text-slate-600 leading-relaxed">
                <p>
                  We are a small, family-run business who loves the Lord God almighty. 
                  We recognize that God commands us to work unto Him. <em className="text-slate-800">"Colossians 3:23 - 
                  And whatever you do, do it heartily, as to the Lord and not to men."</em>
                </p>
                <p>
                  Because of this command, we strive to build the best boats we possibly can. 
                  We believe in high quality boats and desire to be honest, hardworking people 
                  who treat our customers with respect and integrity.
                </p>
                <p>
                  <strong className="text-slate-900">Nick Popp</strong> is the founder of Armageddon Enterprises. 
                  Before becoming a full-time boat builder, Nick was a welding instructor for 16 years. 
                  Nick became a certified welder and is now passing on his passion for welding 
                  and boat building to his family.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                <img 
                  src="https://customer-assets.emergentagent.com/job_c1d7fe75-4e50-41d3-9936-35f1cf314c96/artifacts/orktkvdm_Family%20pic%20%281%29.jpg"
                  alt="The Popp Family"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white p-6 max-w-xs">
                <p className="font-heading text-lg uppercase tracking-wide">The Popp Family</p>
                <p className="text-sm text-orange-100 mt-1">Founders of Armageddon Boats</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why We Build */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-square overflow-hidden bg-slate-200">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_c1d7fe75-4e50-41d3-9936-35f1cf314c96/artifacts/ff0a68ib_P1050334.JPG"
                    alt="Armageddon Boat"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square overflow-hidden bg-slate-200 mt-8">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_c1d7fe75-4e50-41d3-9936-35f1cf314c96/artifacts/hoq2jqcm_1000000381.jpg"
                    alt="Boat on the water"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-orange-500 font-heading text-sm uppercase tracking-widest mb-3">
                Why We Do What We Do
              </p>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                More Than Just Boats
              </h2>
              <div className="space-y-6 text-slate-600 leading-relaxed">
                <p>
                  The Popp family loves to hunt, fish, and run the wild and awesome lakes, 
                  rivers, and seaways that abound in Alaska.
                </p>
                <p>
                  We understand that a boat is not just a toy but a <strong className="text-slate-900">harvesting tool</strong> that 
                  puts food on the table. Our desire is to build the toughest, best-designed 
                  harvesting tool possible at an affordable price.
                </p>
                <p className="text-xl font-heading text-slate-900 uppercase tracking-wide">
                  We build boats that will outlast you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Boat That Started It All */}
      <section className="py-24 bg-white" data-testid="origin-story-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                <img 
                  src="https://customer-assets.emergentagent.com/job_c1d7fe75-4e50-41d3-9936-35f1cf314c96/artifacts/rtd7pely_imagejpeg_0.JPG"
                  alt="The original strip and glue boat"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-orange-500 text-white p-6 max-w-xs">
                <p className="font-heading text-lg uppercase tracking-wide">Where It All Began</p>
                <p className="text-sm text-orange-100 mt-1">A Wedding Gift Built with Love</p>
              </div>
            </div>
            <div>
              <p className="text-orange-500 font-heading text-sm uppercase tracking-widest mb-3">
                Our Beginning
              </p>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                The Boat That Started It All
              </h2>
              <p className="text-xl text-slate-800">
                Nick built this Strip and Glue for his wedding present to his wife Brittany.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-24 bg-slate-50" data-testid="values-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Mission */}
            <div className="bg-slate-900 text-white p-8">
              <div className="w-14 h-14 bg-orange-500 flex items-center justify-center mb-6">
                <Heart className="w-7 h-7" />
              </div>
              <h3 className="font-heading text-2xl font-bold uppercase mb-4">Our Mission</h3>
              <p className="text-slate-300 leading-relaxed">
                Build the best boats that we can and honor God with our work.
              </p>
            </div>

            {/* Values */}
            <div className="bg-slate-50 border-2 border-slate-100 p-8">
              <div className="w-14 h-14 bg-orange-500 flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold uppercase text-slate-900 mb-4">Our Values</h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500"></span>
                  Quality
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500"></span>
                  Integrity
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500"></span>
                  Functionality
                </li>
              </ul>
            </div>

            {/* Expertise */}
            <div className="bg-slate-50 border-2 border-slate-100 p-8">
              <div className="w-14 h-14 bg-orange-500 flex items-center justify-center mb-6">
                <Wrench className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold uppercase text-slate-900 mb-4">Our Expertise</h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500"></span>
                  16+ Years Welding Experience
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500"></span>
                  AWS Certified Welder
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500"></span>
                  Family-Trained Craftsmen
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-orange-500 font-heading text-sm uppercase tracking-widest mb-3">
              Get In Touch
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Contact Information
            </h2>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="bg-slate-800 p-8 md:p-12">
              <div className="text-center mb-8">
                <p className="font-heading text-2xl font-bold text-white uppercase">Nick Popp</p>
                <p className="text-slate-400">Founder, Armageddon Enterprises</p>
              </div>
              
              <div className="space-y-4">
                <a 
                  href="tel:907-415-9639"
                  className="flex items-center justify-center gap-4 p-4 bg-slate-700 hover:bg-slate-600 transition-colors group"
                >
                  <Phone className="w-6 h-6 text-orange-500" />
                  <span className="text-xl text-white group-hover:text-orange-500 transition-colors">
                    907-415-9639
                  </span>
                </a>
                
                <a 
                  href="mailto:akskiffs@gmail.com"
                  className="flex items-center justify-center gap-4 p-4 bg-slate-700 hover:bg-slate-600 transition-colors group"
                >
                  <Mail className="w-6 h-6 text-orange-500" />
                  <span className="text-xl text-white group-hover:text-orange-500 transition-colors">
                    akskiffs@gmail.com
                  </span>
                </a>
              </div>

              <div className="mt-8 pt-8 border-t border-slate-700 text-center">
                <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                  Send Us a Message
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
